import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, Package, CreditCard, DollarSign, Check, AlertCircle, Wifi, Users, Link, Save, Info } from 'lucide-react';

const OnboardingStep2 = ({ wizardData, refData, onSave, onComplete, onBack, onSkip, saving }) => {
  // =====================================================
  // STATE
  // =====================================================
  
  // Bundle payment details (for bundles selected in Step 1)
  const [bundlePaymentDetails, setBundlePaymentDetails] = useState([]);
  
  // ISP perks selected
  const [selectedIspPerks, setSelectedIspPerks] = useState([]);
  
  // Membership details
  const [membershipDetails, setMembershipDetails] = useState([]);
  
  // Nested card perks (cards that include memberships)
  const [nestedCardPerks, setNestedCardPerks] = useState([]);
  
  // Card credit allocations
  const [cardAllocations, setCardAllocations] = useState([]);
  
  // Individual service details (for services NOT in bundles)
  const [serviceDetails, setServiceDetails] = useState([]);
  
  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    bundles: true,       // Show bundle payment details first
    credits: false,
    isp: false,
    memberships: false,
    nestedPerks: false,
    services: false
  });
  
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // =====================================================
  // UTILITY FUNCTIONS (defined early so useMemo can use them)
  // =====================================================
  
  const getMonthlyEquivalent = (monthlyCost, annualCost, billingCycle) => {
    if (billingCycle === 'annual' && annualCost > 0) return annualCost / 12;
    return monthlyCost || 0;
  };

  const getTotalAllocatedForCard = (cardName) => {
    const card = cardAllocations.find(ca => ca.cardName === cardName);
    return card?.allocations.reduce((sum, a) => sum + (a.amount || 0), 0) || 0;
  };

  // =====================================================
  // COMPUTED: Services covered by bundles
  // =====================================================
  const bundledServiceNames = useMemo(() => {
    const services = new Set();
    
    // Get services from user's selected bundles (from wizardData)
    (wizardData.bundles || []).forEach(userBundle => {
      // Find the full bundle data from refData
      const fullBundle = refData.bundles?.find(b => b.bundle_code === userBundle.bundle_code);
      if (fullBundle?.bundle_services) {
        fullBundle.bundle_services.forEach(bs => {
          services.add(bs.service_name);
        });
      }
      // Also check if the bundle itself has bundle_services attached
      if (userBundle.bundle_services) {
        userBundle.bundle_services.forEach(bs => {
          services.add(bs.service_name);
        });
      }
    });
    
    console.log('Bundled service names:', Array.from(services));
    return services;
  }, [wizardData.bundles, refData.bundles]);

  // =====================================================
  // INITIALIZATION
  // =====================================================
  useEffect(() => {
    initializeState();
  }, [wizardData, refData]);

  const initializeState = () => {
    console.log('Initializing Step 2 with wizardData:', wizardData);
    console.log('User bundles from Step 1:', wizardData.bundles);
    
    // =====================================================
    // Compute bundled services locally for initialization
    // =====================================================
    const localBundledServices = new Set();
    (wizardData.bundles || []).forEach(userBundle => {
      const fullBundle = refData.bundles?.find(b => b.bundle_code === userBundle.bundle_code);
      if (fullBundle?.bundle_services) {
        fullBundle.bundle_services.forEach(bs => {
          localBundledServices.add(bs.service_name);
        });
      }
      if (userBundle.bundle_services) {
        userBundle.bundle_services.forEach(bs => {
          localBundledServices.add(bs.service_name);
        });
      }
    });
    
    // =====================================================
    // Initialize BUNDLE payment details (from Step 1 selections)
    // =====================================================
    const initialBundleDetails = (wizardData.bundles || []).map(bundle => {
      const fullBundle = refData.bundles?.find(b => b.bundle_code === bundle.bundle_code);
      return {
        bundleCode: bundle.bundle_code,
        bundleName: bundle.bundle_name || fullBundle?.bundle_name || bundle.bundle_code,
        billingCycle: 'monthly',
        monthlyCost: bundle.monthly_cost || fullBundle?.monthly_cost || 0,
        annualCost: fullBundle?.annual_cost || 0,
        includedServices: fullBundle?.bundle_services?.map(bs => bs.service_name) || 
                         bundle.bundle_services?.map(bs => bs.service_name) || []
      };
    });
    setBundlePaymentDetails(initialBundleDetails);
    
    // =====================================================
    // Initialize INDIVIDUAL service details (EXCLUDING bundled services)
    // =====================================================
    const servicesNotInBundles = (wizardData.services || []).filter(service => 
      !localBundledServices.has(service)
    );
    
    console.log('Services not in bundles:', servicesNotInBundles);
    
    const initialServiceDetails = servicesNotInBundles.map(service => ({
      service,
      tier: '',
      billingCycle: 'monthly',
      monthlyCost: 0,
      annualCost: 0,
      paymentMethod: 'paid',
      paymentSource: '',
      creditApplied: 0
    }));
    setServiceDetails(initialServiceDetails);

    // =====================================================
    // Initialize membership details
    // =====================================================
    const initialMembershipDetails = (wizardData.memberships || []).map(m => {
      const perksForMembership = refData.membershipPerks?.filter(p => p.membership_type === m.membership_type) || [];
      return {
        membershipName: m.membership_name,
        membershipType: m.membership_type,
        billingCycle: 'monthly',
        monthlyCost: 0,
        annualCost: 0,
        availablePerks: perksForMembership,
        selectedPerks: []
      };
    });
    setMembershipDetails(initialMembershipDetails);

    // =====================================================
    // Initialize card allocations
    // =====================================================
    const initialCardAllocations = (wizardData.creditCards || [])
      .filter(card => card.streaming_credit_monthly > 0)
      .map(card => ({
        cardName: card.card_name,
        monthlyCredit: card.streaming_credit_monthly,
        allocations: []
      }));
    setCardAllocations(initialCardAllocations);

    // =====================================================
    // Initialize nested card perks (cards that include memberships)
    // =====================================================
    const cardsWithMemberships = refData.cardPerks?.filter(p => p.perk_type === 'included_membership') || [];
    const initialNestedPerks = cardsWithMemberships
      .filter(cardPerk => wizardData.creditCards?.some(c => c.card_name === cardPerk.card_name))
      .map(cardPerk => {
        const membershipType = getMembershipTypeFromName(cardPerk.included_membership);
        const streamingPerks = refData.membershipPerks?.filter(p => p.membership_type === membershipType) || [];
        return {
          cardName: cardPerk.card_name,
          includedMembership: cardPerk.included_membership,
          membershipType: membershipType,
          membershipUsed: false,
          availableStreamingPerks: streamingPerks,
          selectedStreamingPerk: null
        };
      });
    setNestedCardPerks(initialNestedPerks);
  };

  const getMembershipTypeFromName = (name) => {
    const mapping = {
      'Walmart+': 'walmart_plus',
      'Amazon Prime': 'amazon_prime',
      'Instacart+': 'instacart_plus',
      'DoorDash DashPass': 'dashpass',
      'Kroger Boost': 'kroger_boost'
    };
    return mapping[name] || name.toLowerCase().replace(/[^a-z]/g, '_');
  };

  // =====================================================
  // COMPUTED VALUES
  // =====================================================
  
  // ISP perks available for the user's provider
  const availableIspPerks = useMemo(() => {
    // Get perks for services that aren't already bundled
    return refData.ispPerks?.filter(p => 
      p.isp_name === wizardData.internetProvider && 
      wizardData.services?.includes(p.service_name) &&
      !bundledServiceNames.has(p.service_name) &&
      p.service_name !== 'None'
    ) || [];
  }, [refData.ispPerks, wizardData.internetProvider, wizardData.services, bundledServiceNames]);

  // Services covered by ISP perks
  const ispCoveredServices = selectedIspPerks.map(p => p.service);
  
  // Services covered by membership perks
  const membershipCoveredServices = membershipDetails.flatMap(m => m.selectedPerks.map(p => p.service));
  
  // Services covered by nested card perks
  const nestedPerkCoveredServices = nestedCardPerks
    .filter(np => np.membershipUsed && np.selectedStreamingPerk)
    .map(np => np.selectedStreamingPerk.service);

  // Services that still need individual configuration (not bundled, not free from perks)
  const uncoveredServices = useMemo(() => {
    return (wizardData.services || []).filter(service => 
      !bundledServiceNames.has(service) &&
      !ispCoveredServices.includes(service) &&
      !membershipCoveredServices.includes(service) &&
      !nestedPerkCoveredServices.includes(service)
    );
  }, [wizardData.services, bundledServiceNames, ispCoveredServices, membershipCoveredServices, nestedPerkCoveredServices]);

  // Services eligible for credit allocation (bundles + individual paid services)
  const creditEligibleItems = useMemo(() => {
    const items = [];
    
    // Add bundles
    bundlePaymentDetails.forEach(bundle => {
      items.push({
        name: bundle.bundleName,
        type: 'bundle',
        bundleCode: bundle.bundleCode,
        monthlyCost: getMonthlyEquivalent(bundle.monthlyCost, bundle.annualCost, bundle.billingCycle)
      });
    });
    
    // Add individual services (not free from perks)
    uncoveredServices.forEach(service => {
      items.push({
        name: service,
        type: 'service',
        monthlyCost: serviceDetails.find(sd => sd.service === service)?.monthlyCost || 0
      });
    });
    
    return items;
  }, [bundlePaymentDetails, uncoveredServices, serviceDetails]);

  // =====================================================
  // HANDLERS
  // =====================================================
  
  const markUnsaved = () => setHasUnsavedChanges(true);
  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  // Bundle payment handlers
  const updateBundleCost = (bundleCode, field, value) => {
    markUnsaved();
    setBundlePaymentDetails(prev => prev.map(b => 
      b.bundleCode === bundleCode ? { ...b, [field]: value } : b
    ));
  };

  // ISP perk handlers
  const handleIspPerkToggle = (perk) => {
    markUnsaved();
    const isSelected = selectedIspPerks.some(p => p.service === perk.service_name);
    
    if (isSelected) {
      setSelectedIspPerks(prev => prev.filter(p => p.service !== perk.service_name));
      setServiceDetails(prev => prev.map(sd => {
        if (sd.service === perk.service_name && sd.paymentMethod === 'isp_perk') {
          return { ...sd, paymentMethod: 'paid', paymentSource: '', tier: '' };
        }
        return sd;
      }));
    } else {
      setSelectedIspPerks(prev => [...prev, {
        service: perk.service_name,
        tier: perk.included_tier,
        ispName: perk.isp_name,
        isTrial: perk.is_trial,
        trialMonths: perk.trial_duration_months
      }]);
      setServiceDetails(prev => prev.map(sd => {
        if (sd.service === perk.service_name) {
          return { ...sd, paymentMethod: 'isp_perk', paymentSource: perk.isp_name, tier: perk.included_tier, monthlyCost: 0, annualCost: 0 };
        }
        return sd;
      }));
    }
  };

  // Membership handlers
  const updateMembershipCost = (membershipType, field, value) => {
    markUnsaved();
    setMembershipDetails(prev => prev.map(m => m.membershipType === membershipType ? { ...m, [field]: value } : m));
  };

  const handleMembershipPerkToggle = (membershipType, perk) => {
    markUnsaved();
    setMembershipDetails(prev => prev.map(m => {
      if (m.membershipType === membershipType) {
        const isSelected = m.selectedPerks.some(p => p.service === perk.service_name);
        let newSelectedPerks;
        if (isSelected) {
          newSelectedPerks = m.selectedPerks.filter(p => p.service !== perk.service_name);
        } else if (perk.is_choice && perk.choice_group) {
          newSelectedPerks = m.selectedPerks.filter(p => {
            const perkInfo = m.availablePerks.find(ap => ap.service_name === p.service);
            return perkInfo?.choice_group !== perk.choice_group;
          });
          newSelectedPerks.push({ service: perk.service_name, tier: perk.included_tier, choiceGroup: perk.choice_group });
        } else {
          newSelectedPerks = [...m.selectedPerks, { service: perk.service_name, tier: perk.included_tier }];
        }
        return { ...m, selectedPerks: newSelectedPerks };
      }
      return m;
    }));

    const isNowSelected = !membershipDetails.find(m => m.membershipType === membershipType)?.selectedPerks.some(p => p.service === perk.service_name);
    setServiceDetails(prev => prev.map(sd => {
      if (sd.service === perk.service_name) {
        if (isNowSelected) return { ...sd, paymentMethod: 'membership_perk', paymentSource: membershipType, tier: perk.included_tier, monthlyCost: 0 };
        else if (sd.paymentMethod === 'membership_perk' && sd.paymentSource === membershipType) return { ...sd, paymentMethod: 'paid', paymentSource: '', tier: '' };
      }
      return sd;
    }));
  };

  // Nested perk handlers
  const handleNestedPerkToggle = (cardName, useMembership) => {
    markUnsaved();
    setNestedCardPerks(prev => prev.map(np => {
      if (np.cardName === cardName) {
        if (!useMembership && np.selectedStreamingPerk) {
          setServiceDetails(prevSd => prevSd.map(sd => {
            if (sd.service === np.selectedStreamingPerk.service && sd.paymentMethod === 'nested_perk') {
              return { ...sd, paymentMethod: 'paid', paymentSource: '', tier: '' };
            }
            return sd;
          }));
          return { ...np, membershipUsed: false, selectedStreamingPerk: null };
        }
        return { ...np, membershipUsed: useMembership };
      }
      return np;
    }));
  };

  const handleNestedStreamingPerkSelect = (cardName, perk) => {
    markUnsaved();
    setNestedCardPerks(prev => prev.map(np => {
      if (np.cardName === cardName) {
        if (np.selectedStreamingPerk) {
          setServiceDetails(prevSd => prevSd.map(sd => {
            if (sd.service === np.selectedStreamingPerk.service && sd.paymentMethod === 'nested_perk') {
              return { ...sd, paymentMethod: 'paid', paymentSource: '', tier: '' };
            }
            return sd;
          }));
        }
        if (perk) {
          setServiceDetails(prevSd => prevSd.map(sd => {
            if (sd.service === perk.service_name) {
              return { ...sd, paymentMethod: 'nested_perk', paymentSource: cardName, tier: perk.included_tier, monthlyCost: 0 };
            }
            return sd;
          }));
        }
        return { ...np, selectedStreamingPerk: perk ? { service: perk.service_name, tier: perk.included_tier, choiceGroup: perk.choice_group } : null };
      }
      return np;
    }));
  };

  // Credit allocation handler
  const handleCreditAllocation = (cardName, itemName, itemType, amount) => {
    markUnsaved();
    setCardAllocations(prev => prev.map(ca => {
      if (ca.cardName === cardName) {
        const existingIdx = ca.allocations.findIndex(a => a.name === itemName && a.type === itemType);
        let newAllocations = [...ca.allocations];
        if (existingIdx >= 0) {
          if (amount > 0) newAllocations[existingIdx] = { name: itemName, type: itemType, amount };
          else newAllocations.splice(existingIdx, 1);
        } else if (amount > 0) {
          newAllocations.push({ name: itemName, type: itemType, amount });
        }
        return { ...ca, allocations: newAllocations };
      }
      return ca;
    }));
  };

  // Service detail handler
  const updateServiceDetail = (service, field, value) => {
    markUnsaved();
    setServiceDetails(prev => prev.map(sd => sd.service === service ? { ...sd, [field]: value } : sd));
  };

  // =====================================================
  // SUMMARY CALCULATION
  // =====================================================
  
  const calculateMonthlySummary = () => {
    let totalMonthly = 0;
    const breakdown = { bundles: [], memberships: [], paidServices: [], creditCoveredItems: [], freeServices: [] };

    // Bundles
    bundlePaymentDetails.forEach(bundle => {
      const monthly = getMonthlyEquivalent(bundle.monthlyCost, bundle.annualCost, bundle.billingCycle);
      
      // Check if credits are applied to this bundle
      const creditsApplied = cardAllocations.reduce((sum, ca) => {
        const alloc = ca.allocations.find(a => a.name === bundle.bundleName && a.type === 'bundle');
        return sum + (alloc?.amount || 0);
      }, 0);
      
      const netCost = Math.max(0, monthly - creditsApplied);
      
      if (creditsApplied > 0) {
        breakdown.creditCoveredItems.push({
          name: bundle.bundleName,
          type: 'bundle',
          grossCost: monthly,
          creditApplied: creditsApplied,
          netCost,
          includedServices: bundle.includedServices
        });
      } else {
        breakdown.bundles.push({ 
          name: bundle.bundleName, 
          monthlyCost: monthly, 
          billingCycle: bundle.billingCycle,
          includedServices: bundle.includedServices
        });
      }
      
      totalMonthly += netCost;
    });

    // Memberships
    membershipDetails.forEach(m => {
      if (m.monthlyCost > 0 || m.annualCost > 0) {
        const monthly = getMonthlyEquivalent(m.monthlyCost, m.annualCost, m.billingCycle);
        totalMonthly += monthly;
        breakdown.memberships.push({ 
          name: m.membershipName, 
          monthlyCost: monthly, 
          billingCycle: m.billingCycle, 
          includedServices: m.selectedPerks.map(p => p.service) 
        });
      }
    });

    // Individual services
    serviceDetails.forEach(sd => {
      if (sd.paymentMethod === 'isp_perk') {
        breakdown.freeServices.push({ service: sd.service, reason: `Included with ${wizardData.internetProvider}` });
      } else if (sd.paymentMethod === 'membership_perk') {
        const membership = membershipDetails.find(m => m.membershipType === sd.paymentSource);
        breakdown.freeServices.push({ service: sd.service, reason: `Included with ${membership?.membershipName || sd.paymentSource}` });
      } else if (sd.paymentMethod === 'nested_perk') {
        breakdown.freeServices.push({ service: sd.service, reason: `Included via ${sd.paymentSource}` });
      } else if (sd.paymentMethod === 'paid' && uncoveredServices.includes(sd.service)) {
        const monthly = getMonthlyEquivalent(sd.monthlyCost, sd.annualCost, sd.billingCycle);
        
        // Check for credits
        const creditsApplied = cardAllocations.reduce((sum, ca) => {
          const alloc = ca.allocations.find(a => a.name === sd.service && a.type === 'service');
          return sum + (alloc?.amount || 0);
        }, 0);
        
        if (creditsApplied > 0) {
          const netCost = Math.max(0, monthly - creditsApplied);
          breakdown.creditCoveredItems.push({
            name: sd.service,
            type: 'service',
            grossCost: monthly,
            creditApplied: creditsApplied,
            netCost
          });
          totalMonthly += netCost;
        } else if (monthly > 0) {
          totalMonthly += monthly;
          breakdown.paidServices.push({ service: sd.service, monthlyCost: monthly, tier: sd.tier, billingCycle: sd.billingCycle });
        }
      }
    });

    // Add services covered by bundles to free services
    bundlePaymentDetails.forEach(bundle => {
      bundle.includedServices?.forEach(service => {
        if (!breakdown.freeServices.some(f => f.service === service)) {
          breakdown.freeServices.push({ service, reason: `Included in ${bundle.bundleName}` });
        }
      });
    });

    return { totalMonthly, breakdown };
  };

  const summary = calculateMonthlySummary();

  // =====================================================
  // DATA COMPILATION FOR SAVE
  // =====================================================
  
  const compileData = () => {
    // Compile subscription details (individual services only, not bundled)
    const compiledSubscriptionDetails = serviceDetails.map(sd => ({
      service: sd.service,
      tier: sd.tier || '',
      billingCycle: sd.billingCycle || 'monthly',
      paymentMethod: sd.paymentMethod,
      paymentSource: sd.paymentSource,
      cost: getMonthlyEquivalent(sd.monthlyCost, sd.annualCost, sd.billingCycle),
      creditApplied: 0, // Individual credit tracking handled separately
      actualCost: sd.paymentMethod === 'paid' ? getMonthlyEquivalent(sd.monthlyCost, sd.annualCost, sd.billingCycle) : 0
    }));

    // Compile bundle details
    const compiledBundleDetails = bundlePaymentDetails.map(b => ({
      bundleCode: b.bundleCode,
      bundleName: b.bundleName,
      billingCycle: b.billingCycle,
      monthlyCost: b.monthlyCost,
      annualCost: b.annualCost,
      includedServices: b.includedServices
    }));

    // Compile card allocations (now includes both services and bundles)
    const compiledCardAllocations = cardAllocations.map(ca => ({
      cardName: ca.cardName,
      serviceAllocations: ca.allocations.filter(a => a.type === 'service').map(a => ({ service: a.name, amount: a.amount })),
      bundleAllocations: ca.allocations.filter(a => a.type === 'bundle').map(a => ({ bundle: a.name, amount: a.amount }))
    }));

    // Compile membership perk selections
    const membershipPerkSelections = [];
    membershipDetails.forEach(m => {
      m.selectedPerks.forEach(perk => {
        membershipPerkSelections.push({
          perk_source: m.membershipType,
          service_name: perk.service,
          tier: perk.tier,
          choice_group: perk.choiceGroup || null
        });
      });
    });
    
    // From nested card perks
    nestedCardPerks.forEach(np => {
      if (np.membershipUsed && np.selectedStreamingPerk) {
        membershipPerkSelections.push({
          perk_source: np.membershipType,
          service_name: np.selectedStreamingPerk.service,
          tier: np.selectedStreamingPerk.tier,
          choice_group: np.selectedStreamingPerk.choiceGroup || null
        });
      }
    });
    
    // ISP perks
    const ispPerkSelections = selectedIspPerks.map(perk => ({
      perk_source: perk.ispName?.toLowerCase().replace(/\s+/g, '_') + '_internet',
      service_name: perk.service,
      tier: perk.tier
    }));

    return {
      bundles: compiledBundleDetails,
      subscriptionDetails: compiledSubscriptionDetails,
      cardAllocations: compiledCardAllocations,
      membershipPerkSelections,
      ispPerkSelections
    };
  };

  const handleSaveProgress = () => {
    if (onSave) {
      onSave(compileData());
      setHasUnsavedChanges(false);
    }
  };

  const handleCompleteSetup = () => {
    onComplete(compileData());
    setHasUnsavedChanges(false);
  };

  // =====================================================
  // UI COMPONENTS
  // =====================================================
  
  const SectionHeader = ({ title, icon: Icon, section, badge, subtitle }) => (
    <button onClick={() => toggleSection(section)} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-purple-600" />
        <div className="text-left">
          <span className="font-semibold text-gray-900">{title}</span>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        {badge && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">{badge}</span>}
      </div>
      {expandedSections[section] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
    </button>
  );

  const BillingCycleToggle = ({ value, onChange }) => (
    <div className="flex rounded-lg border border-gray-300 overflow-hidden">
      <button type="button" onClick={() => onChange('monthly')} className={`px-3 py-1 text-sm ${value === 'monthly' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'}`}>Monthly</button>
      <button type="button" onClick={() => onChange('annual')} className={`px-3 py-1 text-sm ${value === 'annual' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'}`}>Annual</button>
    </div>
  );

  // =====================================================
  // RENDER
  // =====================================================
  
  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Payment Details</h2>
        <p className="text-gray-600 mt-1">Let's figure out your actual costs and savings</p>
      </div>

      {hasUnsavedChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2 text-yellow-800 text-sm">
          <AlertCircle className="w-4 h-4" />You have unsaved changes
        </div>
      )}

      {/* =====================================================
          SECTION 1: YOUR BUNDLES (Payment details for selected bundles)
          ===================================================== */}
      {bundlePaymentDetails.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <SectionHeader 
            title="Your Bundles" 
            icon={Package} 
            section="bundles" 
            badge={`${bundlePaymentDetails.length} bundle${bundlePaymentDetails.length !== 1 ? 's' : ''}`}
            subtitle="Enter payment details for your bundles" 
          />
          {expandedSections.bundles && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <p className="text-sm text-gray-600">Enter what you pay for each bundle:</p>
              
              {bundlePaymentDetails.map(bundle => (
                <div key={bundle.bundleCode} className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-semibold text-gray-900">{bundle.bundleName}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Includes: {bundle.includedServices?.join(', ') || 'Multiple services'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-green-600">
                      <Check className="w-4 h-4" />
                      <span className="text-sm font-medium">Selected</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-purple-200">
                    <BillingCycleToggle 
                      value={bundle.billingCycle} 
                      onChange={(cycle) => updateBundleCost(bundle.bundleCode, 'billingCycle', cycle)} 
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">$</span>
                      <input 
                        type="number" 
                        step="0.01" 
                        value={bundle.billingCycle === 'annual' ? (bundle.annualCost || '') : (bundle.monthlyCost || '')} 
                        onChange={(e) => updateBundleCost(
                          bundle.bundleCode, 
                          bundle.billingCycle === 'annual' ? 'annualCost' : 'monthlyCost', 
                          parseFloat(e.target.value) || 0
                        )} 
                        className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500" 
                        placeholder="0.00" 
                      />
                      <span className="text-sm text-gray-600">/{bundle.billingCycle === 'annual' ? 'year' : 'month'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          SECTION 2: CREDIT CARD STREAMING CREDITS
          ===================================================== */}
      {cardAllocations.length > 0 && creditEligibleItems.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <SectionHeader 
            title="Credit Card Streaming Credits" 
            icon={CreditCard} 
            section="credits" 
            subtitle="Allocate your card credits to bundles or services" 
          />
          {expandedSections.credits && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <p className="text-sm text-gray-600">
                Your credit cards have streaming credits you can apply to your bundles or services:
              </p>
              
              {cardAllocations.map(card => {
                const totalAllocated = getTotalAllocatedForCard(card.cardName);
                const remaining = card.monthlyCredit - totalAllocated;
                
                return (
                  <div key={card.cardName} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-medium text-gray-900">{card.cardName}</div>
                      <div className={`text-sm font-medium ${remaining < 0 ? 'text-red-600' : remaining === 0 ? 'text-gray-600' : 'text-green-600'}`}>
                        ${remaining.toFixed(2)} of ${card.monthlyCredit}/mo remaining
                      </div>
                    </div>
                    
                    {remaining < 0 && (
                      <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Over-allocated by ${Math.abs(remaining).toFixed(2)}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      {creditEligibleItems.map(item => {
                        const allocation = card.allocations.find(a => a.name === item.name && a.type === item.type);
                        return (
                          <div key={`${item.type}-${item.name}`} className="flex items-center gap-3">
                            <span className="flex-1 text-sm text-gray-700">
                              {item.name}
                              {item.type === 'bundle' && (
                                <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Bundle</span>
                              )}
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-gray-500">$</span>
                              <input 
                                type="number" 
                                step="0.01" 
                                min="0" 
                                value={allocation?.amount || ''} 
                                onChange={(e) => handleCreditAllocation(card.cardName, item.name, item.type, parseFloat(e.target.value) || 0)} 
                                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500" 
                                placeholder="0.00" 
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          SECTION 3: ISP PERKS
          ===================================================== */}
      {availableIspPerks.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <SectionHeader 
            title="Internet Provider Perks" 
            icon={Wifi} 
            section="isp" 
            badge={selectedIspPerks.length > 0 ? `${selectedIspPerks.length} selected` : null} 
            subtitle={`From ${wizardData.internetProvider}`} 
          />
          {expandedSections.isp && (
            <div className="p-4 border-t border-gray-200 space-y-2">
              <p className="text-sm text-gray-600 mb-2">These services may be included with your internet plan:</p>
              {availableIspPerks.map(perk => {
                const isSelected = selectedIspPerks.some(p => p.service === perk.service_name);
                return (
                  <label key={`${perk.isp_name}-${perk.service_name}`} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'}`}>
                    <input type="checkbox" checked={isSelected} onChange={() => handleIspPerkToggle(perk)} className="w-5 h-5 text-green-600 rounded focus:ring-green-500" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{perk.service_name}</div>
                      <div className="text-sm text-gray-600">{perk.included_tier}{perk.is_trial && ` • ${perk.trial_duration_months}-month trial`}</div>
                    </div>
                    {isSelected && <span className="text-green-600 font-medium text-sm">FREE</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          SECTION 4: NESTED CARD PERKS
          ===================================================== */}
      {nestedCardPerks.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <SectionHeader 
            title="Credit Card Membership Perks" 
            icon={Link} 
            section="nestedPerks" 
            badge={nestedCardPerks.filter(np => np.membershipUsed).length > 0 ? `${nestedCardPerks.filter(np => np.membershipUsed).length} active` : null} 
            subtitle="Memberships included with your cards" 
          />
          {expandedSections.nestedPerks && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <p className="text-sm text-gray-600 mb-2">Some of your credit cards include memberships with streaming perks:</p>
              {nestedCardPerks.map(np => {
                // Only show perks for services not in bundles
                const relevantPerks = np.availableStreamingPerks.filter(perk => 
                  wizardData.services?.includes(perk.service_name) && 
                  !bundledServiceNames.has(perk.service_name)
                );
                
                if (relevantPerks.length === 0 && !np.membershipUsed) return null;
                
                return (
                  <div key={np.cardName} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-gray-900">{np.cardName}</span>
                      <span className="text-gray-400">→</span>
                      <span className="text-purple-600">{np.includedMembership}</span>
                    </div>
                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer mb-3">
                      <input type="checkbox" checked={np.membershipUsed} onChange={(e) => handleNestedPerkToggle(np.cardName, e.target.checked)} className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500" />
                      <span className="text-gray-700">I use the {np.includedMembership} from this card</span>
                    </label>
                    {np.membershipUsed && relevantPerks.length > 0 && (
                      <div className="ml-8 space-y-2">
                        <p className="text-sm text-gray-600">{np.includedMembership} includes streaming — which do you use?</p>
                        {relevantPerks.map(perk => (
                          <label key={perk.service_name} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${np.selectedStreamingPerk?.service === perk.service_name ? 'bg-green-100 border border-green-500' : 'bg-gray-50 hover:bg-gray-100'}`}>
                            <input type="radio" name={`nested-${np.cardName}`} checked={np.selectedStreamingPerk?.service === perk.service_name} onChange={() => handleNestedStreamingPerkSelect(np.cardName, perk)} className="w-4 h-4 text-green-600 focus:ring-green-500" />
                            <div><span className="text-gray-900">{perk.service_name}</span><span className="text-sm text-gray-500 ml-2">({perk.included_tier})</span></div>
                          </label>
                        ))}
                        <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${np.membershipUsed && !np.selectedStreamingPerk ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'}`}>
                          <input type="radio" name={`nested-${np.cardName}`} checked={np.membershipUsed && !np.selectedStreamingPerk} onChange={() => handleNestedStreamingPerkSelect(np.cardName, null)} className="w-4 h-4 text-gray-600 focus:ring-gray-500" />
                          <span className="text-gray-600">I don't use the streaming perk</span>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          SECTION 5: MEMBERSHIPS
          ===================================================== */}
      {membershipDetails.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <SectionHeader 
            title="Memberships" 
            icon={Users} 
            section="memberships" 
            badge={membershipDetails.filter(m => m.selectedPerks.length > 0).length > 0 ? `${membershipDetails.filter(m => m.selectedPerks.length > 0).length} with perks` : null} 
            subtitle="Amazon Prime, Walmart+, etc." 
          />
          {expandedSections.memberships && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              {membershipDetails.map(membership => {
                const isCoveredByCard = nestedCardPerks.some(np => np.includedMembership === membership.membershipName && np.membershipUsed);
                if (isCoveredByCard) {
                  return (
                    <div key={membership.membershipType} className="p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-purple-600" />
                        <span className="text-purple-700">{membership.membershipName} is included with your credit card</span>
                      </div>
                    </div>
                  );
                }
                
                // Only show perks for services not in bundles
                const relevantPerks = membership.availablePerks.filter(p => 
                  wizardData.services?.includes(p.service_name) &&
                  !bundledServiceNames.has(p.service_name)
                );
                
                return (
                  <div key={membership.membershipType} className="border border-gray-200 rounded-lg p-4">
                    <div className="font-medium text-gray-900 mb-3">{membership.membershipName}</div>
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600 mb-2">What do you pay for {membership.membershipName}?</div>
                      <div className="flex items-center gap-4">
                        <BillingCycleToggle value={membership.billingCycle} onChange={(cycle) => updateMembershipCost(membership.membershipType, 'billingCycle', cycle)} />
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">$</span>
                          <input type="number" step="0.01" value={membership.billingCycle === 'annual' ? (membership.annualCost || '') : (membership.monthlyCost || '')} onChange={(e) => updateMembershipCost(membership.membershipType, membership.billingCycle === 'annual' ? 'annualCost' : 'monthlyCost', parseFloat(e.target.value) || 0)} className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500" placeholder="0.00" />
                          <span className="text-sm text-gray-600">/{membership.billingCycle === 'annual' ? 'year' : 'month'}</span>
                        </div>
                      </div>
                    </div>
                    {relevantPerks.length > 0 && (
                      <div>
                        <div className="text-sm text-gray-600 mb-2">Streaming included with {membership.membershipName}:</div>
                        <div className="space-y-2">
                          {relevantPerks.map(perk => {
                            const isSelected = membership.selectedPerks.some(p => p.service === perk.service_name);
                            return (
                              <label key={perk.service_name} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${isSelected ? 'bg-green-100 border border-green-500' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                <input type={perk.is_choice ? 'radio' : 'checkbox'} name={perk.is_choice ? `membership-${membership.membershipType}-${perk.choice_group}` : undefined} checked={isSelected} onChange={() => handleMembershipPerkToggle(membership.membershipType, perk)} className="w-4 h-4 text-green-600 focus:ring-green-500" />
                                <div className="flex-1"><span className="text-gray-900">{perk.service_name}</span><span className="text-sm text-gray-500 ml-2">({perk.included_tier})</span>{perk.is_choice && <span className="text-xs text-purple-600 ml-2">(choose one)</span>}</div>
                                {isSelected && <span className="text-green-600 font-medium text-sm">Included</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          SECTION 6: SERVICE DETAILS & COSTS (all services)
          ===================================================== */}
      {(wizardData.services?.length > 0) && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <SectionHeader 
            title="Service Details & Costs" 
            icon={DollarSign} 
            section="services" 
            badge={uncoveredServices.length > 0 ? `${uncoveredServices.length} to configure` : null} 
            subtitle="Review all your streaming services" 
          />
          {expandedSections.services && (
            <div className="p-4 border-t border-gray-200 space-y-3">
              <p className="text-sm text-gray-600">Review your streaming services. Bundled services are shown for reference but don't need configuration.</p>
              
              {/* Sort services: bundled first, then uncovered */}
              {[...wizardData.services].sort((a, b) => {
                const aIsBundled = bundledServiceNames.has(a);
                const bIsBundled = bundledServiceNames.has(b);
                if (aIsBundled && !bIsBundled) return -1;
                if (!aIsBundled && bIsBundled) return 1;
                return a.localeCompare(b);
              }).map(service => {
                const isBundled = bundledServiceNames.has(service);
                const sd = serviceDetails.find(s => s.service === service);
                
                // Find which bundle includes this service
                const includingBundle = isBundled 
                  ? bundlePaymentDetails.find(b => b.includedServices?.includes(service))
                  : null;
                
                // BUNDLED SERVICE - Grayed out, non-editable
                if (isBundled) {
                  return (
                    <div key={service} className="border border-gray-200 rounded-lg p-4 bg-gray-50 opacity-75">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-500">{service}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Package className="w-4 h-4 text-purple-500" />
                            <span className="text-sm text-purple-600">
                              Included in {includingBundle?.bundleName || 'bundle'}
                            </span>
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                          Bundled
                        </span>
                      </div>
                    </div>
                  );
                }
                
                // Check if covered by a perk
                if (sd?.paymentMethod === 'isp_perk' || sd?.paymentMethod === 'membership_perk' || sd?.paymentMethod === 'nested_perk') {
                  return (
                    <div key={service} className="border border-green-200 rounded-lg p-4 bg-green-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-700">{service}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Check className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-700">
                              Free with {sd.paymentSource}
                            </span>
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                          Included
                        </span>
                      </div>
                    </div>
                  );
                }
                
                // INDIVIDUAL SERVICE - Editable
                if (!sd) return null;
                
                return (
                  <div key={service} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-medium text-gray-900">{service}</div>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        Individual
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Tier/Plan</label>
                        <input 
                          type="text" 
                          value={sd.tier} 
                          onChange={(e) => updateServiceDetail(service, 'tier', e.target.value)} 
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" 
                          placeholder="e.g., Premium, Standard with Ads" 
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Cost</label>
                        <div className="flex items-center gap-2">
                          <BillingCycleToggle 
                            value={sd.billingCycle} 
                            onChange={(cycle) => updateServiceDetail(service, 'billingCycle', cycle)} 
                          />
                          <span className="text-gray-600">$</span>
                          <input 
                            type="number" 
                            step="0.01" 
                            value={sd.billingCycle === 'annual' ? (sd.annualCost || '') : (sd.monthlyCost || '')} 
                            onChange={(e) => updateServiceDetail(service, sd.billingCycle === 'annual' ? 'annualCost' : 'monthlyCost', parseFloat(e.target.value) || 0)} 
                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500" 
                            placeholder="0.00" 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          MONTHLY SUMMARY
          ===================================================== */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Monthly Summary</h3>
        <div className="space-y-2 text-sm">
          {/* Bundles */}
          {summary.breakdown.bundles.map((bundle, idx) => (
            <div key={idx} className="flex justify-between">
              <div>
                <span className="text-gray-600">{bundle.name}</span>
                <span className="text-xs text-gray-400 ml-2">({bundle.includedServices?.length || 0} services)</span>
              </div>
              <span className="font-medium">${bundle.monthlyCost.toFixed(2)}/mo</span>
            </div>
          ))}
          
          {/* Memberships */}
          {summary.breakdown.memberships.map((m, idx) => (
            <div key={idx} className="flex justify-between">
              <div>
                <span className="text-gray-600">{m.name}</span>
                {m.includedServices.length > 0 && <span className="text-xs text-green-600 ml-2">(includes {m.includedServices.join(', ')})</span>}
              </div>
              <span className="font-medium">${m.monthlyCost.toFixed(2)}/mo</span>
            </div>
          ))}
          
          {/* Paid services */}
          {summary.breakdown.paidServices.map((s, idx) => (
            <div key={idx} className="flex justify-between">
              <span className="text-gray-600">{s.service}{s.tier && <span className="text-gray-400 ml-1">({s.tier})</span>}</span>
              <span className="font-medium">${s.monthlyCost.toFixed(2)}/mo</span>
            </div>
          ))}
          
          {/* Credit-covered items */}
          {summary.breakdown.creditCoveredItems.map((item, idx) => (
            <div key={idx} className="flex justify-between">
              <span className="text-gray-600">
                {item.name}
                <span className="text-green-600 ml-1">(${item.creditApplied.toFixed(2)} credit)</span>
              </span>
              <span className="font-medium">{item.netCost > 0 ? `$${item.netCost.toFixed(2)}/mo` : 'Covered'}</span>
            </div>
          ))}
          
          {/* Free services */}
          {summary.breakdown.freeServices.length > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <div className="text-green-600 font-medium mb-1">Included at no extra cost:</div>
              {summary.breakdown.freeServices.map((s, idx) => (
                <div key={idx} className="flex justify-between text-green-700">
                  <span>{s.service}</span>
                  <span className="text-xs">{s.reason}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Total */}
          <div className="pt-3 border-t-2 border-gray-300 mt-3">
            <div className="flex justify-between text-lg font-bold">
              <span>Total Monthly</span>
              <span>${summary.totalMonthly.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          NAVIGATION BUTTONS
          ===================================================== */}
      <div className="sticky bottom-0 bg-white pt-4 pb-2 -mx-6 px-6 sm:mx-0 sm:px-0 sm:static border-t border-gray-200 sm:border-0">
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <button onClick={onBack} className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors">
              Back
            </button>
            <button onClick={handleSaveProgress} disabled={saving} className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
              <Save className="w-5 h-5" />{saving ? 'Saving...' : 'Save Progress'}
            </button>
          </div>
          <button onClick={handleCompleteSetup} disabled={saving} className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
            <Check className="w-5 h-5" />{saving ? 'Saving...' : 'Complete Setup'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep2;